import consumer from "../channels/consumer";
import type { LiveDiceRollFeed, LiveDiceRollFeedMember, LiveDiceRollFeedRoll, LiveDiceRollFeedTeaser } from "../types/generated/live";
import { isOwnRoll } from "./feed";
import { csrfToken } from "./roll_and_post";

export type FeedStatus = "loading" | "live" | "offline" | "failed";

export type FeedState = {
	status: FeedStatus;
	rolls: LiveDiceRollFeedRoll[];
	teaser: LiveDiceRollFeedTeaser | null;
	viewerAccountId: string | null;
	canDelete: boolean;
	members: LiveDiceRollFeedMember[] | null;
};

export type FeedEvent = { type: "state" } | { type: "arrived"; roll: LiveDiceRollFeedRoll; own: boolean; live: boolean } | { type: "error"; reason: string };

export type FeedMessage =
	| { type: "roll"; roll: LiveDiceRollFeedRoll; previous: string | null; at: string }
	| { type: "removed"; id: string; newest: string | null; at: string }
	| { type: "teaser"; rolls: LiveDiceRollFeedRoll[]; teaser: LiveDiceRollFeedTeaser | null; at: string }
	| { type: "revoked" };

type Handlers = { connected(): void; disconnected(): void; rejected(): void; received(message: FeedMessage): void };
type Channel = (campaignId: string, handlers: Handlers) => { unsubscribe(): void };

export type FeedClientOptions = {
	// The campaign's rolls JSON; the destroy endpoint is the same path without the extension.
	url: string;
	initial?: LiveDiceRollFeed;
	// The viewer's @handle without the "@", as the dice feed names their own rolls.
	handle?: string | null;
	// Seams for tests.
	channel?: Channel;
	fetch?: typeof fetch;
	csrf?: () => string;
	target?: EventTarget;
	visible?: () => boolean;
};

// What the server keeps and serves; a push never grows the list past it.
const WINDOW = 100;
const RETRY_FIRST = 2000;
const RETRY_MAX = 30000;
const GONE = [401, 403, 404];

const actionCable: Channel = (campaignId, handlers) => consumer.subscriptions.create({ channel: "DiceRollsChannel", campaign_id: campaignId }, handlers);

const registry = new Map<string, { client: ReturnType<typeof createClient>; count: number }>();

// One client per campaign per page; every holder calls `release` once, and the last one unsubscribes. A client that gave up
// (`failed`) is stopped for good, so the next acquire replaces it, for every holder at once: a retry is a release and a new acquire.
export function feedClient(campaignId: string, options: FeedClientOptions) {
	let entry = registry.get(campaignId);
	if (!entry) {
		entry = { client: createClient(campaignId, options), count: 0 };
		registry.set(campaignId, entry);
	} else if (entry.client.state.status === "failed") {
		entry.client.destroy();
		entry.client = createClient(campaignId, options);
	}
	entry.count += 1;
	const held = entry;
	let released = false;
	return {
		get state() {
			return held.client.state;
		},
		subscribe: (listener: (event: FeedEvent) => void) => held.client.subscribe(listener),
		remove: (id: string) => held.client.remove(id),
		release() {
			if (released) return;
			released = true;
			held.count -= 1;
			if (held.count > 0) return;
			registry.delete(campaignId);
			held.client.destroy();
		},
	};
}

function createClient(
	campaignId: string,
	{
		url,
		initial,
		handle,
		channel = actionCable,
		fetch: send = (...args) => globalThis.fetch(...args),
		csrf = csrfToken,
		target = globalThis,
		visible = () => !document.hidden,
	}: FeedClientOptions,
) {
	let state: FeedState = { status: "loading", rolls: [], teaser: null, viewerAccountId: null, canDelete: false, members: null };
	const seen = new Set<string>();
	// Rolls with a DELETE in flight stay out of every state until the server answers.
	const hidden = new Set<string>();
	const listeners = new Set<(event: FeedEvent) => void>();
	let loaded = false;
	let stopped = false;
	let generation = 0;
	let inflight: AbortController | undefined;
	let fetching = false;
	// The reconciliation a refetch is after, kept until a snapshot resolves it, retries included. A quiet one outranks a loud one.
	let reconcile: "loud" | "quiet" | null = null;
	// The oldest roll of the last snapshot when that snapshot was cut short (the full window, or a teaser with rolls hidden), so
	// anything unseen at or before it is older history, not news. A pushed roll, a deletion or an empty list never move it.
	let floor: LiveDiceRollFeedRoll | null = null;
	let tries = 0;
	// ISO 8601 with fixed-width microseconds, so the stamps compare as strings.
	let snapshotAt = "";
	let lastAt = "";
	let timer: ReturnType<typeof setTimeout> | undefined;
	// From the page coming back online until a snapshot lands: the socket may still hold rolls made during the drop.
	let returning = false;

	const emit = (event: FeedEvent) => {
		for (const listener of [...listeners]) listener(event);
	};
	const set = (patch: Partial<FeedState>) => {
		state = { ...state, ...patch };
		emit({ type: "state" });
	};
	const newest = () => state.rolls.at(-1)?.id ?? null;

	// The feed's order, `(created_at, id)`, which the server keeps to the microsecond.
	const after = (roll: LiveDiceRollFeedRoll, mark: LiveDiceRollFeedRoll | null) =>
		!mark || roll.created_at > mark.created_at || (roll.created_at === mark.created_at && roll.id > mark.id);

	// A roll that arrives is news; the ones of the first load are history. `live` says it was made while the page was here to see
	// it: a push brought it (unless it came before the snapshot after an offline spell), or a refetch that a gap in the pushes started (rolls made at the same moment reach the channel out of
	// order) found it after the floor. Older ones are backfill, a refetch after a reconnect or a tab's return found everything
	// after the fact, and a view that plays rolls plays only the first kind.
	function announce(rolls: LiveDiceRollFeedRoll[], live: boolean) {
		for (const roll of rolls) {
			if (seen.has(roll.id)) continue;
			seen.add(roll.id);
			if (loaded) emit({ type: "arrived", roll, own: roll.kind === "account" && isOwnRoll(roll, handle), live: live && after(roll, floor) });
		}
	}

	const shown = (rolls: LiveDiceRollFeedRoll[]) => rolls.filter((roll) => !hidden.has(roll.id));

	function apply(feed: LiveDiceRollFeed, live: boolean) {
		const rolls = shown(feed.rolls);
		announce(rolls, live);
		floor = feed.rolls.length >= WINDOW || (feed.teaser?.hidden_count ?? 0) > 0 ? (feed.rolls[0] ?? null) : null;
		loaded = true;
		returning = false;
		snapshotAt = lastAt = feed.at;
		set({ status: "live", rolls, teaser: feed.teaser, viewerAccountId: feed.viewer_account_id, canDelete: feed.can_delete, members: feed.members });
	}

	function fail() {
		stopped = true;
		generation++;
		fetching = false;
		reconcile = null;
		clearTimeout(timer);
		inflight?.abort();
		set({ status: "failed" });
	}

	async function refetch(live = false): Promise<void> {
		if (stopped) return;
		clearTimeout(timer);
		inflight?.abort();
		const mine = ++generation;
		if (!live) reconcile = "quiet";
		else if (reconcile === null) reconcile = "loud";
		fetching = true;
		const controller = new AbortController();
		inflight = controller;
		try {
			const response = await send(url, { headers: { Accept: "application/json" }, credentials: "same-origin", cache: "no-store", signal: controller.signal });
			if (mine !== generation) return;
			if (GONE.includes(response.status)) return fail();
			if (!response.ok) throw new Error(String(response.status));
			const feed = (await response.json()) as LiveDiceRollFeed;
			if (mine !== generation) return;
			fetching = false;
			tries = 0;
			const playing = reconcile === "loud";
			reconcile = null;
			apply(feed, playing);
		} catch {
			if (mine !== generation) return;
			fetching = false;
			set({ status: "offline" });
			if (visible()) timer = setTimeout(() => void refetch(reconcile === "loud"), Math.min(RETRY_FIRST * 2 ** tries++, RETRY_MAX));
		}
	}

	// A message that lands while a fetch is out may be newer than its answer, so the fetch starts over after it.
	function received(message: FeedMessage) {
		if (stopped) return;
		if (message.type !== "revoked") {
			if (message.at <= snapshotAt) return;
			if (message.at < lastAt) return void refetch(true);
		}
		const wasFetching = fetching;
		switch (message.type) {
			case "roll":
				if (hidden.has(message.roll.id) || state.rolls.some((roll) => roll.id === message.roll.id)) return;
				if (message.previous !== newest()) return void refetch(true);
				announce([message.roll], !returning);
				set({ rolls: [...state.rolls, message.roll].slice(-WINDOW) });
				break;
			case "removed":
				hidden.delete(message.id);
				set({ rolls: state.rolls.filter((roll) => roll.id !== message.id) });
				if (message.newest !== newest()) return void refetch(true);
				break;
			case "teaser":
				announce(shown(message.rolls), !returning);
				set({ rolls: shown(message.rolls), teaser: message.teaser });
				break;
			case "revoked":
				return fail();
		}
		lastAt = message.at;
		if (wasFetching) refetch(true);
	}

	const subscription = channel(campaignId, {
		connected() {
			if (stopped) return;
			void refetch();
		},
		disconnected() {
			if (!stopped && loaded) set({ status: "offline" });
		},
		rejected: fail,
		received,
	});

	const online = () => {
		returning = true;
		void refetch();
	};
	const offline = () => !stopped && loaded && set({ status: "offline" });
	const wake = () => {
		if (visible()) void refetch();
	};
	target.addEventListener("online", online);
	target.addEventListener("offline", offline);
	target.addEventListener("visibilitychange", wake);

	if (initial) apply(initial, false);
	else void refetch();

	return {
		get state() {
			return state;
		},
		subscribe(listener: (event: FeedEvent) => void) {
			listeners.add(listener);
			return () => void listeners.delete(listener);
		},
		// Hides the roll at once; if the server refuses, the refetched snapshot decides whether it comes back.
		async remove(id: string): Promise<void> {
			if (!state.rolls.some((roll) => roll.id === id)) return;
			hidden.add(id);
			set({ rolls: state.rolls.filter((other) => other.id !== id) });
			let reason: string | null = null;
			try {
				const response = await send(`${url.replace(/\.json$/, "")}/${id}`, {
					method: "DELETE",
					credentials: "same-origin",
					headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": csrf() },
				});
				if (!response.ok) reason = String(response.status);
			} catch {
				reason = "network";
			}
			// A removed push during the request already confirmed the delete.
			const confirmed = !hidden.delete(id);
			if (reason === null || confirmed || stopped) return;
			emit({ type: "error", reason });
			void refetch();
		},
		destroy() {
			stopped = true;
			generation++;
			clearTimeout(timer);
			inflight?.abort();
			subscription.unsubscribe();
			target.removeEventListener("online", online);
			target.removeEventListener("offline", offline);
			target.removeEventListener("visibilitychange", wake);
			listeners.clear();
		},
	};
}
